import ModeEditOutlineOutlinedIcon from "@mui/icons-material/ModeEditOutlineOutlined";
import { Button, ButtonProps, SxProps, Theme } from "@mui/material";
import { ReactNode } from "react";
import {
  CHART_COLORS,
  CHART_DIMENSIONS,
  CHART_TYPOGRAPHY,
} from "./provider/patient/chart";
import { AddIcon } from "../svg-icons/material-icons";

type ActionButtonVariant =
  "primary" | "secondary" | "ghost" | "outlined" | "contained" | "text";

type StyledButtonV2Props = {
  label?: ReactNode;
  variant?: ActionButtonVariant;
  shouldAddIcon?: boolean;
  shouldEditIcon?: boolean;
  eIcon?: ReactNode;
  sIcon?: ReactNode;
  sx?: SxProps<Theme>;
} & Omit<ButtonProps, "variant">;

export const BUTTON_BASE_SX: SxProps<Theme> = {
  height: CHART_DIMENSIONS.controlHeight,
  borderRadius: CHART_DIMENSIONS.borderRadius,
  ...CHART_TYPOGRAPHY.body5,
  fontFamily: CHART_TYPOGRAPHY.fontFamily,
  fontWeight: CHART_TYPOGRAPHY.weight.regular,
  textTransform: "none",
  whiteSpace: "nowrap",
  px: 1.5,
  "& .MuiButton-startIcon svg, & .MuiButton-endIcon svg": {
    fontSize: CHART_DIMENSIONS.iconSm,
  },
};

const VARIANT_MAP: Record<
  ActionButtonVariant,
  { muiVariant: ButtonProps["variant"]; sx: SxProps<Theme> }
> = {
  primary: {
    muiVariant: "contained",
    sx: {
      bgcolor: CHART_COLORS.btnPrimaryBg,
      color: CHART_COLORS.surfaceWhite,
      "&:hover": { bgcolor: CHART_COLORS.btnPrimaryHoverBg },
    },
  },
  secondary: {
    muiVariant: "outlined",
    sx: {
      bgcolor: CHART_COLORS.btnSecondaryBg,
      color: CHART_COLORS.btnSecondaryText,
      borderColor: CHART_COLORS.btnSecondaryText,
      "& .MuiButton-startIcon svg": { color: CHART_COLORS.btnSecondaryText },
      "&:hover": {
        bgcolor: CHART_COLORS.btnSecondaryHoverBg,
        borderColor: CHART_COLORS.btnSecondaryText,
      },
    },
  },
  ghost: {
    muiVariant: "outlined",
    sx: {
      bgcolor: CHART_COLORS.surfaceWhite,
      color: CHART_COLORS.btnGhostText,
      borderColor: CHART_COLORS.btnGhostBorder,
      "&:hover": {
        borderColor: CHART_COLORS.btnPrimaryBg,
        bgcolor: CHART_COLORS.btnGhostHoverBg,
      },
    },
  },
  outlined: {
    muiVariant: "outlined",
    sx: {},
  },
  contained: {
    muiVariant: "contained",
    sx: {},
  },
  text: {
    muiVariant: "text",
    sx: {
      ":hover": {
        bgcolor: "transparent",
      },
    },
  },
};

const StyledButtonV2 = ({
  label,
  variant = "primary",
  shouldAddIcon,
  shouldEditIcon,
  sIcon,
  eIcon,
  startIcon,
  sx,
  ...others
}: StyledButtonV2Props) => {
  const v = VARIANT_MAP[variant];

  return (
    <Button
      variant={v.muiVariant}
      disableElevation
      startIcon={
        sIcon ||
        startIcon ||
        (shouldAddIcon && <AddIcon />) ||
        (shouldEditIcon && <ModeEditOutlineOutlinedIcon />) ||
        undefined
      }
      endIcon={eIcon}
      {...others}
      sx={{
        ...(BUTTON_BASE_SX as object),
        ...(v.sx as object),
        ...(sx as object),
      }}
    >
      {label}
    </Button>
  );
};

export default StyledButtonV2;
